/**
 * Unified Chart Data Fetching Hook
 * Centralizes data fetching patterns across all chart components
 * Eliminates duplicate useEffect patterns and provides consistent error handling
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { dataSourceManager, DataSourceType } from '../services/DataSourceManager';
import { dataValidationService } from '../services/DataValidationService';
import { log } from '../services/LoggingService';

export interface ChartDataState<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  lastFetchTime: number | null;
  retryCount: number;
}

export interface ChartDataOptions {
  enabled?: boolean;
  retryOnError?: boolean;
  maxRetries?: number;
  retryDelay?: number;
  validationEnabled?: boolean;
  preferredSource?: DataSourceType;
  forceRefresh?: boolean;
  timeout?: number;
}

export interface ChartDataActions {
  refetch: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Unified hook for chart data fetching
 * Provides consistent loading states, error handling, and retry logic
 */
export function useChartData<T>(
  fetchFunction: () => Promise<T>,
  dependencies: any[] = [],
  options: ChartDataOptions = {}
): ChartDataState<T> & ChartDataActions {
  const {
    enabled = true,
    retryOnError = true,
    maxRetries = 3,
    retryDelay = 1000,
    validationEnabled = true,
    preferredSource = DataSourceType.CACHE,
    forceRefresh = false,
    timeout = 10000
  } = options;

  const [state, setState] = useState<ChartDataState<T>>({
    data: null,
    isLoading: false,
    error: null,
    lastFetchTime: null,
    retryCount: 0
  });

  const isCancelledRef = useRef(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState({
      data: null,
      isLoading: false,
      error: null,
      lastFetchTime: null,
      retryCount: 0
    });
  }, []);

  const refetch = useCallback(async () => {
    if (!enabled) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const startTime = Date.now();
      const result = await fetchFunction();
      const fetchTime = Date.now() - startTime;

      if (isCancelledRef.current) return;

      // Validate data if enabled
      if (validationEnabled && result) {
        const validation = dataValidationService.validateApiResponse(result as any);
        if (!validation.isValid) {
          log.warn(`Data validation failed: ${validation.errors.join(', ')}`, "DataValidation");
        }
        if (validation.warnings.length > 0) {
          log.warn(`Data validation warnings: ${validation.warnings.join(', ')}`, "DataValidation");
        }
      }

      setState(prev => ({
        ...prev,
        data: result,
        isLoading: false,
        error: null,
        lastFetchTime: Date.now(),
        retryCount: 0
      }));

      log.debug(`Data fetched successfully in ${fetchTime}ms`, "DataFetch");
    } catch (error) {
      if (isCancelledRef.current) return;

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      log.error(`Data fetch failed: ${errorMessage}`, "DataFetch", error);

      setState(prev => {
        const newRetryCount = prev.retryCount + 1;
        const shouldRetry = retryOnError && newRetryCount <= maxRetries;

        if (shouldRetry) {
          // Schedule retry
          retryTimeoutRef.current = setTimeout(() => {
            refetch();
          }, retryDelay * newRetryCount); // Exponential backoff
        }

        return {
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error : new Error(errorMessage),
          retryCount: newRetryCount
        };
      });
    }
  }, [enabled, fetchFunction, validationEnabled, retryOnError, maxRetries, retryDelay]);

  // Main effect for data fetching
  useEffect(() => {
    if (!enabled) return;

    isCancelledRef.current = false;
    refetch();

    return () => {
      isCancelledRef.current = true;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, dependencies);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isCancelledRef.current = true;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return {
    ...state,
    refetch,
    clearError,
    reset
  };
}

/**
 * Specialized hook for API endpoint data fetching
 * Uses DataSourceManager for intelligent data source selection
 */
export function useApiData<T>(
  endpoint: string,
  params: Record<string, any> = {},
  dependencies: any[] = [],
  options: ChartDataOptions = {}
): ChartDataState<T> & ChartDataActions {
  // Stabilize params object by serializing to prevent infinite loops from object reference changes
  const stableParams = useMemo(() => params, [JSON.stringify(params)]);

  // Stabilize options to prevent re-renders
  const stableOptions = useMemo(() => options, [
    options.forceRefresh,
    options.preferredSource,
    options.timeout
  ]);

  const fetchFunction = useCallback(async () => {
    const result = await dataSourceManager.getData<T>(endpoint, stableParams, {
      forceRefresh: stableOptions.forceRefresh,
      preferredSource: stableOptions.preferredSource || DataSourceType.CACHE,
      timeout: stableOptions.timeout || 10000
    });

    if (result.status === 'error') {
      throw new Error(result.metadata?.errorMessage || 'Failed to fetch data');
    }

    return result.data;
  }, [endpoint, stableParams, stableOptions]);

  return useChartData(fetchFunction, dependencies, options);
}

/**
 * Specialized hook for service-based data fetching
 * For components that use specific data services
 */
export function useServiceData<T>(
  serviceFunction: () => Promise<T>,
  dependencies: any[] = [],
  options: ChartDataOptions = {}
): ChartDataState<T> & ChartDataActions {
  return useChartData(serviceFunction, dependencies, options);
}
